import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'rc-calendar/lib/Picker';
import Calendar from 'rc-calendar';
import enUS from 'rc-calendar/lib/locale/en_US';
import 'rc-calendar/assets/index.css';
import 'rc-time-picker/assets/index.css';
import './DatePicker.scss';

import moment from 'moment';
import 'moment/locale/zh-cn';
import 'moment/locale/en-gb';

const monthFormat = 'MMMM YYYY';
const dayFormat = 'LL';

const buildMonthMoment = (year, monthIndex, reference) => {
  const next = moment({ year, month: monthIndex, day: 1 }).startOf('day');
  if (reference?.utcOffset) {
    next.utcOffset(reference.utcOffset());
  }
  return next;
};

const isMonthAfterLimit = (monthMoment, monthLimit) =>
  monthMoment.clone().startOf('month').isAfter(monthLimit.clone().startOf('month'), 'month');

const MonthGridPicker = ({ value, monthLimit, onChange }) => {
  const limit = monthLimit || moment().startOf('month');
  const [panelYear, setPanelYear] = useState(() => (value ? value.year() : limit.year()));

  useEffect(() => {
    if (value?.year()) {
      setPanelYear(value.year());
    }
  }, [value]);

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) => {
        const monthMoment = buildMonthMoment(panelYear, monthIndex, limit);
        return {
          monthMoment,
          label: monthMoment.format('MMM'),
          disabled: isMonthAfterLimit(monthMoment, limit),
          selected:
            Boolean(value) &&
            value.year() === panelYear &&
            value.month() === monthIndex &&
            !isMonthAfterLimit(monthMoment, limit)
        };
      }),
    [limit, panelYear, value]
  );

  const canGoNextYear = panelYear < limit.year();

  return (
    <div className="att-tt-month-grid">
      <div className="att-tt-month-grid__header">
        <button
          type="button"
          className="att-tt-month-grid__nav"
          aria-label="Previous year"
          onClick={() => setPanelYear((year) => year - 1)}
        >
          «
        </button>
        <span className="att-tt-month-grid__year">{panelYear}</span>
        <button
          type="button"
          className="att-tt-month-grid__nav"
          aria-label="Next year"
          disabled={!canGoNextYear}
          onClick={() => {
            if (canGoNextYear) setPanelYear((year) => year + 1);
          }}
        >
          »
        </button>
      </div>

      <div className="att-tt-month-grid__body">
        {months.map(({ monthMoment, label, disabled, selected }) => (
          <button
            key={`${panelYear}-${label}`}
            type="button"
            disabled={disabled}
            className={`att-tt-month-grid__month${selected ? ' is-selected' : ''}${
              disabled ? ' is-disabled' : ''
            }`}
            onClick={() => {
              if (!disabled) onChange(monthMoment);
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

const DatePickerApp = ({ initialDate, handleChangeDate, type, maxMonth }) => {
  const [value, setValue] = useState(initialDate);
  const [open, setOpen] = useState(false);

  const monthLimit = useMemo(() => {
    if (!maxMonth) return moment().startOf('month');
    return moment(maxMonth).startOf('month');
  }, [maxMonth]);

  useEffect(() => {
    setValue(initialDate);
  }, [initialDate]);

  useEffect(() => {
    setOpen(false);
  }, [type]);

  const applyChange = (rawValue) => {
    if (!rawValue) return;
    // Build from the picker's literal calendar fields so a non-IST browser can't
    // shift the selected month/day across a timezone boundary (e.g. May -> April).
    const next =
      type === 'Month'
        ? moment({ year: rawValue.year(), month: rawValue.month(), date: 1 }).startOf('day')
        : moment({ year: rawValue.year(), month: rawValue.month(), date: rawValue.date() }).startOf(
            'day'
          );

    if (type === 'Month' && next.clone().startOf('month').isAfter(monthLimit, 'month')) {
      return;
    }

    setValue(next);
    handleChangeDate(next);
    if (type === 'Month') {
      setOpen(false);
    }
  };

  const onChange = (newValue) => {
    applyChange(newValue);
  };

  const onOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const getWeekString = (val) => {
    if (!val) return '';
    return (
      moment(val).startOf('week').format('DD MMM YYYY') +
      ' - ' +
      moment(val).endOf('week').format('DD MMM YYYY')
    );
  };

  const getDisplayValue = () => {
    if (!value) return '';
    switch (type) {
      case 'Day':
        return value.format('DD MMM YYYY');
      case 'Week':
        return getWeekString(value);
      case 'Month':
        return value.format(monthFormat);
      default:
        return '';
    }
  };

  const dateRender = (current) => {
    if (
      type === 'Week' &&
      value &&
      current.year() === value.year() &&
      current.week() === value.week()
    ) {
      return (
        <div className="rc-calendar-selected-day">
          <div className="rc-calendar-date">{current.date()}</div>
        </div>
      );
    }
    return <div className="rc-calendar-date">{current.date()}</div>;
  };

  const disabledDate = (current) => {
    if (!current) return false;

    if (type === 'Day' || type === 'Week') {
      const limit = maxMonth ? moment(maxMonth) : moment();
      const todayEnd = limit.clone().endOf('day').hour(23).minute(59).second(59);
      return current.valueOf() > todayEnd.valueOf();
    }

    if (type === 'Month') {
      return current.clone().startOf('month').isAfter(monthLimit, 'month');
    }

    return false;
  };

  const calendar = useMemo(() => {
    if (type === 'Month') {
      return (
        <div className="att-tt-month-grid-wrap">
          <MonthGridPicker value={value} monthLimit={monthLimit} onChange={onChange} />
        </div>
      );
    }

    if (type === 'Week') {
      return (
        <Calendar
          key="week"
          className="att-tt-calendar-week"
          locale={enUS}
          format={dayFormat}
          value={value}
          showDateInput={false}
          disabledDate={disabledDate}
          dateRender={dateRender}
          onChange={onChange}
        />
      );
    }

    return (
      <Calendar
        key="day"
        className="att-tt-calendar-day"
        locale={enUS}
        format={dayFormat}
        defaultValue={value}
        showDateInput={false}
        disabledDate={disabledDate}
      />
    );
  }, [type, value, monthLimit]);

  return (
    <div className={`att-tt-rc-datepicker att-tt-rc-datepicker--${String(type).toLowerCase()}`}>
      <DatePicker
        value={value}
        onChange={onChange}
        animation="slide-up"
        calendar={calendar}
        open={open}
        onOpenChange={onOpenChange}
      >
        {() => (
          <span tabIndex="0">
            <input
              placeholder={
                type === 'Month' ? 'Select month' : type === 'Week' ? 'Select week' : 'Select date'
              }
              readOnly
              className="ant-calendar-picker-input ant-input"
              tabIndex="-1"
              value={getDisplayValue()}
            />
          </span>
        )}
      </DatePicker>
    </div>
  );
};

export default DatePickerApp;
